import { SMS_PROVIDERS } from 'modules/graphql/types';
import { useSelector } from 'react-redux';
import { Edit24, Add24, Disconnect, Lock } from 'ui/icons';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { OverflowMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements } from 'ui/actionMenu/types';
import { LeftTooltip } from 'ui/tooltip';
import styled from 'styled-components';
import {
  selectFeatureFlags,
  selectIsBossOrSalesBossLevel,
  selectIsSmsVerificationFeatureFlagOn,
} from 'core/userContext';
import {
  SMS_SENDER_TERMS_BY_SERVICE_PROVIDER,
  SMS_VERIFICATION_PROFILE_TERM,
  SendingPlatform,
} from '../../../constants';
import { useSendingPlatformsQueryContext, useModalsContext } from '../../../contexts';
import { getSendingPlatformForEspAccount, isIterableSMSSendingPlatform } from '../../../utils';

export const TEXT = {
  ID: 'sms-account-actions-menu',
  ADD_SMS_SENDER: {
    ID: 'sms-account-add-sender-action',
  },
  ADD_VERIFICATION_PROFILE: {
    ID: 'sms-account-add-verification-profile-action',
  },
  EDIT_ACTION: {
    LABEL: 'Edit account',
    ID: 'sms-account-edit-action',
  },
  DISCONNECT_ACTION: {
    LABEL: 'Disconnect account',
    ID: 'sms-account-delete-action',
    TOOLTIP:
      'Before you can disconnect this account, you’ll need to reassign or remove any of the message services it relies on. For help, contact support@iterable.com.',
  },
};

const disconnectTooltipText = (serviceProvider: SMS_PROVIDERS) =>
  `Before you can disconnect this account, you’ll need to reassign or remove any of the ${SMS_SENDER_TERMS_BY_SERVICE_PROVIDER[serviceProvider]}s it relies on. For help, contact support@iterable.com.`;

const smsVerificationTooltipText =
  'Adds support for SMS verification. Reach out to support@iterable.com to enable.';

type SmsOverflowMenuProps = {
  espAccountId: number;
};

const NoCursor = styled.p`
  cursor: not-allowed;
`;

const DisabledWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
  align-items: center;
  cursor: not-allowed;
`;

const DisabledLabel = styled.p`
  text-align: left;
  width: ${pxToRem(206)};
`;

const DisabledText = styled.span`
  text-align: center;
`;

export const SmsOverflowMenu = ({ espAccountId }: SmsOverflowMenuProps) => {
  const {
    showEditAccountModal,
    showDisconnectAccountModal,
    showAddSmsSenderModal,
    showAddVerificationProfileModal,
  } = useModalsContext();
  const { selectEspAccount, hasSenders } = useSendingPlatformsQueryContext();
  const isBossOrSalesBossLevel = useSelector(selectIsBossOrSalesBossLevel);
  const { SMSSelfServeAccountSetupUI } = useSelector(selectFeatureFlags);
  const espAccount = selectEspAccount(espAccountId);
  if (!espAccount) return null;

  const { esp: espAny } = espAccount;
  const esp = espAny as SMS_PROVIDERS;

  const isAddingVerificationEnabled = useSelector(selectIsSmsVerificationFeatureFlagOn);
  const sendingPlatform = getSendingPlatformForEspAccount(espAccount) as SendingPlatform;
  const isEditDisabled = isIterableSMSSendingPlatform(sendingPlatform)
    ? !SMSSelfServeAccountSetupUI || !isBossOrSalesBossLevel // Iterable SMS enablement requires Boss
    : false; // If a customer is already enabled, they can edit their account

  const options = [
    {
      content: (
        <>
          <Add24 />
          <span>{`Add ${SMS_SENDER_TERMS_BY_SERVICE_PROVIDER[esp]}`}</span>
        </>
      ),
      id: TEXT.ADD_SMS_SENDER.ID,
      onClick: () => showAddSmsSenderModal(espAccountId),
      as: AllowedActionMenuElements.Button,
    },
    {
      content: (
        <>
          <Lock />
          <LeftTooltip
            tooltipContent={smsVerificationTooltipText}
            isEnabled={!isAddingVerificationEnabled}
          >
            <NoCursor>{`Add ${SMS_VERIFICATION_PROFILE_TERM}`}</NoCursor>
          </LeftTooltip>
        </>
      ),
      id: TEXT.ADD_VERIFICATION_PROFILE.ID,
      onClick: () => showAddVerificationProfileModal(espAccountId),
      as: AllowedActionMenuElements.Button,
      isDisabled: !isAddingVerificationEnabled,
    },
    {
      content: (
        <>
          <Edit24 />
          <span>{TEXT.EDIT_ACTION.LABEL}</span>
        </>
      ),
      id: TEXT.EDIT_ACTION.ID,
      onClick: () => showEditAccountModal(espAccountId, sendingPlatform),
      as: AllowedActionMenuElements.Button,
      appendDivider: true,
      isDisabled: isEditDisabled,
    },
    {
      content: hasSenders(espAccountId) ? (
        <LeftTooltip tooltipContent={<DisabledText>{disconnectTooltipText(esp)}</DisabledText>}>
          <DisabledWrapper>
            <Disconnect />
            <DisabledLabel>{TEXT.DISCONNECT_ACTION.LABEL}</DisabledLabel>
          </DisabledWrapper>
        </LeftTooltip>
      ) : (
        <>
          <Disconnect />
          <span>{TEXT.DISCONNECT_ACTION.LABEL}</span>
        </>
      ),
      id: TEXT.DISCONNECT_ACTION.ID,
      onClick: () => showDisconnectAccountModal(espAccountId),
      as: AllowedActionMenuElements.Button,
      isDisabled: hasSenders(espAccountId),
    },
  ];

  return <OverflowMenu id={TEXT.ID} options={options} />;
};
